import {
  filter,
  isEmpty,
  isObject,
  map,
  reverse,
  size,
  trim,
  values,
} from 'lodash';
import React, { useEffect, useState } from 'react';
import LocationHelper from '../../../common/helpers/domain/LocationHelper';
import { useWindowSize } from '../../../common/helpers/utility/useWindowSize';
import projectResource from '../../../locales/th/project.json';
import ShowMore from '../../showMore/ShowMore';
import { getProjectInfomationConfig } from './project-information-config';
import {
  ProjectInformationAttribute,
  ProjectInformationAttributeData,
  ProjectInformationContent,
  ProjectInformationContentWrapper,
  ProjectInformationLabel,
  ProjectInformationTitle,
  ProjectInformationWrapper,
} from './ProjectInformation.style';
import { ProjectLocationData } from '../../../common/analytics/adapters/projectAdapter';

interface InfoItem {
  label: string;
  value: string;
  hasLink: boolean;
  link?: string;
}
interface Location {
  zone: ProjectLocationData[];
  district: ProjectLocationData[];
  road: ProjectLocationData[];
}

interface PriceTypes {
  level: string;
}
interface ProjectInformationData {
  location: Location;
  transportations: {
    label: string;
    value: string;
    link: string;
  }[];
  priceTypes: PriceTypes;
  developer: string;
  statusDevelopment: {
    status: string;
    label: string;
  };
  completionDate: string;
  startDate: string;
}

interface ProjectInformationProps {
  projectInformationData: ProjectInformationData;
}
const ProjectInformation: React.FC<ProjectInformationProps> = ({
  projectInformationData,
}) => {
  const [showInformationFields, setShowInformationFields] = useState<
    InfoItem[]
  >([]);
  const [isShowMore, setIsShowMore] = useState<boolean>(false);
  const showMoreText = projectResource.showMore;
  const projectInformationTitle = projectResource.projectInformation.title;
  const windowSize = useWindowSize();
  const {
    location = { district: [], zone: [], road: [] },
    transportations = [],
    priceTypes = { level: '' },
    developer = {},
    statusDevelopment = {},
    completionDate = '',
    startDate = '',
  } = projectInformationData;

  const { district = [], zone = [], road = [] } = location;
  const { level = '' } = priceTypes;

  const projectInformationFieldsLocation = getProjectInfomationConfig(
    LocationHelper.formatDynamicZone(zone),
    LocationHelper.formatDynamicDistrict(district),
    LocationHelper.formatDynamicRoad(road),
    LocationHelper.formatDynamicTransportation(transportations),
    level,
    developer,
    statusDevelopment,
    completionDate,
    startDate,
  );

  const projectInformationFields = filter(
    projectInformationFieldsLocation,
    (projectInfo) =>
      projectInfo.value !== null &&
      projectInfo.value !== '' &&
      trim(projectInfo.value),
  );

  useEffect(() => {
    setShowInformationFields(projectInformationFields as InfoItem[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize]);

  const showMoreInfo = () => {
    setShowInformationFields(projectInformationFields as InfoItem[]);
    setIsShowMore(false);
  };

  const renderItem = (infoItem: InfoItem) => {
    if (isObject(infoItem.value)) {
      const separator = ' ,';
      return map(
        reverse(values(infoItem.value)),
        function (v: InfoItem, i: number) {
          return infoItem.hasLink ? (
            <span key={i}>
              <a href={v.link} data-test={'project-information-link'}>
                {v.label}
              </a>
              {size(infoItem.value) !== i + 1 ? separator : ''}
            </span>
          ) : (
            v.label
          );
        },
      );
    }

    return infoItem.hasLink ? (
      <a href={infoItem.link} data-test={'project-information-link'}>
        {infoItem.value}
      </a>
    ) : (
      infoItem.value
    );
  };

  const renderProjectInformationAttribute = (
    infoItem: InfoItem,
    index: number,
  ) => {
    return (
      trim(infoItem.value) && (
        <ProjectInformationAttribute key={index}>
          <ProjectInformationLabel data-test={'project-information-label'}>
            {infoItem.label}
          </ProjectInformationLabel>
          <ProjectInformationAttributeData
            data-test={'project-information-attribute-data'}
          >
            {renderItem(infoItem)}
          </ProjectInformationAttributeData>
        </ProjectInformationAttribute>
      )
    );
  };

  let informationFields: InfoItem[] = [];
  if (!isEmpty(showInformationFields)) {
    informationFields = showInformationFields;
  } else if (!isEmpty(projectInformationFields)) {
    informationFields = projectInformationFields as InfoItem[];
  }

  return (
    !isEmpty(informationFields) && (
      <ProjectInformationWrapper data-test={'project-information'}>
        <ProjectInformationContentWrapper>
          <ProjectInformationTitle>
            {projectInformationTitle}
          </ProjectInformationTitle>
          <ProjectInformationContent>
            {map(informationFields, (infoItem, index) => {
              return renderProjectInformationAttribute(infoItem, index);
            })}
          </ProjectInformationContent>
          {isShowMore && (
            <ShowMore
              isUsedInProjectPage={true}
              showMoreText={showMoreText}
              className="detail-show-more"
              showMoreInfo={() => showMoreInfo()}
            />
          )}
        </ProjectInformationContentWrapper>
      </ProjectInformationWrapper>
    )
  );
};

export default ProjectInformation;
